import { Injectable } from '@angular/core';

import { ActivityDescription, ActivityPerson, ActivityReason } from '../../mru/models/activity-description.model';
import { ActionType, Activity, Comment, Mention, User } from '../mru/activity.model';
import { MruConfigService } from '../mru/mru-config.service';
import { DateService } from '../services/date.service';
import { TimestampFormatService, TimestampType } from '../services/timestamp-format.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { LocService } from '../shared-context/loc.service';
import { HtmlEscaper } from '../utilities/html-escaper.utility';

const enum ActivityPrefix {
    Commented = 'Commented',
    Edited = 'Edited',
    Mentioned = 'Mentioned',
    RepliedYourComment = 'RepliedYourComment',
    RepliedOtherComment = 'RepliedOtherComment'
}

const enum ActivityPersonCount {
    None = '',
    OneOther = 'OneOther',
    Others = 'Others'
}

const enum ActivityTimestampType {
    DayAndTimeOfWeek = 'DayAndTimeOfWeek',
    DayOfWeek = 'DayOfWeek',
    FullDate = 'FullDate',
    FullDateThisYear = 'FullDateThisYear',
    HoursAgo = 'HoursAgo',
    JustNow = 'JustNow',
    MinAgo = 'MinAgo',
    NoDate = 'NoDate',
    Yesterday = 'Yesterday',
    YesterdayAt = 'YesterdayAt'
}

@Injectable({
    providedIn: 'root'
})
export class ActivityDescriptionService {

    private readonly userEmail: string;

    constructor(
        startupConfigService: ClientStartupConfigService,
        private readonly timestampFormatService: TimestampFormatService,
        private readonly locService: LocService,
        private readonly mruConfigService: MruConfigService,
        private readonly dateService: DateService
    ) {
        this.userEmail = startupConfigService.config.userPrincipalName;
    }

    public activityDescription(activities: Array<Activity>, itemLastViewedTime: Date, shortDate: boolean, noDate: boolean = false): ActivityDescription {
        if (!activities || activities.length === 0) {
            return null;
        }

        activities = this.filterActivities(activities, itemLastViewedTime);

        const activityMap = new Map<ActionType, Array<Activity>>();
        for (let i = 0; i < activities.length; i++) {
            const activity = activities[i];
            const actionType = activity.action.type;
            if (activityMap.has(actionType)) {
                activityMap.get(actionType).push(activity);
            } else {
                activityMap.set(actionType, [activity]);
            }
        }

        // If modifying this logic, check to see if you need to modify displayedActivityType as well.
        if (activityMap.has(ActionType.Mention)) {
            return this.createMentionDescription(activityMap.get(ActionType.Mention), shortDate, noDate);
        } else if (activityMap.has(ActionType.Comment)) {
            return this.createCommentDescription(activityMap.get(ActionType.Comment), shortDate, noDate);
        } else if (activityMap.has(ActionType.Edit)) {
            return this.createEditDescription(activityMap.get(ActionType.Edit), shortDate, noDate);
        }

        return null;
    }

    // Returns the ActionType that would be rendered in activity description
    public displayedActivityType(activities: Array<Activity>, itemLastViewedTime: Date, filterActivities: boolean): ActionType {
        if (!activities || activities.length === 0) {
            return ActionType.None;
        }

        if (filterActivities) {
            activities = this.filterActivities(activities, itemLastViewedTime);
        }

        let numMentions = 0;
        let numComments = 0;
        let numEdits = 0;
        for (let i = 0; i < activities.length; i++) {
            const actionType = activities[i].action.type;
            if (actionType === ActionType.Mention) {
                numMentions++;
            } else if (actionType === ActionType.Comment) {
                numComments++;
            } else if (actionType === ActionType.Edit) {
                numEdits++;
            }
        }

        if (numMentions > 0) {
            return ActionType.Mention;
        }

        if (numComments > 0) {
            return ActionType.Comment;
        }

        if (numEdits > 0) {
            return ActionType.Edit;
        }

        return ActionType.None;
    }

    public filterActivities(activities: Array<Activity>, itemLastViewedTime: Date): Array<Activity> {
        if (!activities) {
            return [];
        }

        return activities.filter(activity => {
            if (activity && activity.action && activity.timestamp && activity.actor && activity.actor.user && activity.actor.user.email && activity.actor.user.displayName) {
                const action = activity.action;

                // filter out activities that happened before the user last viewed the document. Allow all activities if there is no last viewed time
                if (itemLastViewedTime && activity.timestamp < itemLastViewedTime) {
                    return false;
                }

                // Filter out activities that were done by this user
                if (this.isCurrentUser(activity.actor.user)) {
                    return false;
                }

                // Filter to Mentions that include this user
                if (action.type === ActionType.Mention && action.data) {
                    const mentionees = (action.data as Mention).mentionees;
                    return mentionees && mentionees.filter(m => m && m.user && this.isCurrentUser(m.user)).length > 0;
                }

                return action.type !== ActionType.Unknown;
            }

            return false;
        });
    }

    public isCurrentUser(user: User): boolean {
        return user && (!!user.self || user.userPrincipalName === this.userEmail);
    }

    private createMentionDescription(mentions: Array<Activity>, shortDate: boolean, noDate: boolean): ActivityDescription {
        if (!(mentions && mentions.length)) {
            return null;
        }

        const firstMention = mentions[0];
        const numDistinctActors = this.numDistinctActors(mentions);
        const firstMentionUser = firstMention.actor.user;
        const timestamp = firstMention.timestamp;
        const formatString = this.formatStringWithTime(timestamp, ActivityPrefix.Mentioned, shortDate, noDate, [firstMentionUser.displayName], numDistinctActors - 1);

        return {
            formatString,
            plainText: HtmlEscaper.stripTags(formatString),
            people: [{ upn: firstMentionUser.email, tag: 'c0', name: firstMentionUser.displayName }, { upn: this.userEmail, tag: 'c1', name: this.userEmail }],
            reasonType: ActivityReason.Mention,
            timestamp
        };
    }

    private createCommentDescription(comments: Array<Activity>, shortDate: boolean, noDate: boolean): ActivityDescription {
        if (comments.length === 0) {
            return null;
        }

        const firstComment = comments[0];
        const firstCommentData = firstComment.action.data as Comment;
        const firstCommentUser = firstComment.actor.user;
        const timestamp = firstComment.timestamp;

        let formatString = '';
        let people: Array<ActivityPerson> = null;
        if (comments.length > 1) {
            const numDistinctActors = this.numDistinctActors(comments);
            formatString = this.formatStringWithTime(timestamp, ActivityPrefix.Commented, shortDate, noDate, [firstCommentUser.displayName], numDistinctActors - 1);
            people = [{ upn: firstCommentUser.email, tag: 'c0', name: firstCommentUser.displayName}];
        } else {
            if (firstCommentData && firstCommentData.isReply && firstCommentData.parentAuthor && firstCommentData.parentAuthor.user.displayName && firstCommentData.parentAuthor.user.email) {
                const parentAuthor = firstCommentData.parentAuthor.user;
                if (this.isCurrentUser(parentAuthor)) {
                    formatString = this.formatStringWithTime(timestamp, ActivityPrefix.RepliedYourComment, shortDate, noDate, [firstCommentUser.displayName], 0);
                    people = [{ upn: firstCommentUser.email, tag: 'c0', name: firstCommentUser.displayName }, { upn: this.userEmail, tag: 'c1', name: this.userEmail }];
                } else {
                    formatString = this.formatStringWithTime(timestamp, ActivityPrefix.RepliedOtherComment, shortDate, noDate, [firstCommentUser.displayName, parentAuthor.displayName], 0);
                    people = [{ upn: firstCommentUser.email, tag: 'c0', name: firstCommentUser.displayName }, { upn: parentAuthor.email, tag: 'c1', name: parentAuthor.displayName }];
                }
            } else {
                formatString = this.formatStringWithTime(timestamp, ActivityPrefix.Commented, shortDate, noDate, [firstCommentUser.displayName], 0);
                people = [{ upn: firstCommentUser.email, tag: 'c0', name: firstCommentUser.displayName }];
            }
        }

        return {
            formatString,
            plainText: HtmlEscaper.stripTags(formatString),
            people,
            reasonType: ActivityReason.Comment,
            timestamp
        };
    }

    private createEditDescription(edits: Array<Activity>, shortDate: boolean, noDate: boolean): ActivityDescription {
        if (edits.length === 0) {
            return null;
        }

        const firstEdit = edits[0];
        const numDistinctActors = this.numDistinctActors(edits);
        const firstEditUser = firstEdit.actor.user;
        const timestamp = firstEdit.timestamp;
        const formatString = this.formatStringWithTime(timestamp, ActivityPrefix.Edited, shortDate, noDate, [firstEditUser.displayName], numDistinctActors - 1);

        return {
            formatString,
            plainText: HtmlEscaper.stripTags(formatString),
            people: [{ upn: firstEditUser.email, tag: 'c0', name: firstEditUser.displayName }],
            reasonType: ActivityReason.Edit,
            timestamp
        };
    }

    /**
     * Creates a formatted string for the given activity information. <c*> tags are still present in the string
     * @param time timestamp of activity
     * @param prefix The activity resource string prefix
     * @param shortDate Flag to use short date format
     * @param noDate Flag to not include date in formatted string
     * @param placeholders Array of format string placeholders. These must be in order from the beginning of the strings. Omit placeholders for the timestamp and 'others' as those
     *                     will be added automatically.
     * @param numOthers The number of 'other' people to show in the string. Ex: for the string 'John and 1 other commented', numOthers should be 1.
     */
    private formatStringWithTime(time: Date, prefix: ActivityPrefix, shortDate: boolean, noDate: boolean, placeholders: Array<string>, numOthers: number): string {
        const othersPrefix = numOthers <= 0 ? ActivityPersonCount.None : (numOthers === 1 ? ActivityPersonCount.OneOther : ActivityPersonCount.Others);
        let key: string = prefix + othersPrefix;
        const timestampType = noDate ? ActivityTimestampType.NoDate : this.getActivityTimestampType(time, shortDate);
        if (timestampType === ActivityTimestampType.FullDateThisYear) {
            key = key + ActivityTimestampType.FullDate;
        } else {
            key = key + timestampType;
        }

        const timestampPlaceholders = this.getTimestampPlaceholders(time, timestampType);
        const formatString = this.mruConfigService.config.activityStrings[key];
        if (othersPrefix === ActivityPersonCount.OneOther || othersPrefix === ActivityPersonCount.None) {
            placeholders = placeholders.concat(timestampPlaceholders);
        } else {
            placeholders.push(this.locService.localizedNumberString(numOthers));
            placeholders = placeholders.concat(timestampPlaceholders);
        }

        return this.locService.format(formatString, ...placeholders);
    }

    private getActivityTimestampType(time: Date, shortDate: boolean): ActivityTimestampType {
        const timestampType = this.timestampFormatService.getTimestampType(time);
        switch (timestampType) {
            case TimestampType.None:
                return ActivityTimestampType.NoDate;
            case TimestampType.JustNow:
                return ActivityTimestampType.JustNow;
            case TimestampType.LessThanOneHour:
                return ActivityTimestampType.MinAgo;
            case TimestampType.Today:
                return ActivityTimestampType.HoursAgo;
            case TimestampType.Yesterday:
                return shortDate ? ActivityTimestampType.Yesterday : ActivityTimestampType.YesterdayAt;
            case TimestampType.ThisWeek:
                return shortDate ? ActivityTimestampType.DayOfWeek : ActivityTimestampType.DayAndTimeOfWeek;
            case TimestampType.ThisYear:
                return ActivityTimestampType.FullDateThisYear;
            case TimestampType.FullDate:
                return ActivityTimestampType.FullDate;
        }
    }

    private getTimestampPlaceholders(time: Date, timestampType: ActivityTimestampType): Array<string> {
        const timespan = this.dateService.timespanInSeconds(time);
        switch (timestampType) {
            case ActivityTimestampType.NoDate:
            case ActivityTimestampType.JustNow:
            case ActivityTimestampType.Yesterday:
                return null;
            case ActivityTimestampType.MinAgo:
                return [this.locService.localizedNumberString(Math.round(timespan / 60))];
            case ActivityTimestampType.HoursAgo:
                return [this.locService.localizedNumberString(Math.round(timespan / 3600))];
            case ActivityTimestampType.YesterdayAt:
                return [this.locService.formatTimeFromDate(time)];
            case ActivityTimestampType.DayOfWeek:
                return [this.locService.localizedWeekday(time)];
            case ActivityTimestampType.DayAndTimeOfWeek:
                return [this.locService.localizedWeekday(time), this.locService.formatTimeFromDate(time)];
            case ActivityTimestampType.FullDateThisYear:
                return [this.locService.localizedMonthDate(time)];
            case ActivityTimestampType.FullDate:
                return [this.locService.localizedDateStringMedium(time)];
        }
    }

    private numDistinctActors(activities: Array<Activity>): number {
        const emailSet: Set<string> = new Set(activities.map(a => a.actor.user.email));
        return emailSet.size;
    }
}
